import ForceUpdateBanner from '@/components/force-update/force-update-banner';
import ForceUpdateModal from '@/components/force-update/force-update-modal';
import { forceUpdateMessages } from '@/components/force-update/force-update.messages';
import { type Dispatch, type RootState } from '@/store';
import { isTauri } from '@/tauri/utils';
import { POLICY_EVALUATION } from '@/types/forceUpdatePolicy';
import { formatUtcDate } from '@/utils/dates';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const ForceUpdate = () => {
  const dispatch = useDispatch<Dispatch>();
  const intl = useIntl();
  const evaluationResult = useSelector(
    (state: RootState) => state.forceUpdateModel.evaluationResult
  );
  const currentPolicy = useSelector(
    (state: RootState) => state.forceUpdateModel.currentPolicy
  );
  const isInstalling = useSelector(
    (state: RootState) => state.forceUpdateModel.isInstalling
  );
  useEffect(() => {
    if (!isTauri()) return;
    void dispatch.forceUpdateModel.startPeriodicCheck();
    return () => {
      void dispatch.forceUpdateModel.stopPeriodicCheck();
    };
  }, [dispatch]);

  const handleUpdateNow = () => {
    void dispatch.forceUpdateModel.handleUpdateNow();
  };

  if (!isTauri()) return null;

  if (evaluationResult === POLICY_EVALUATION.FORCED) {
    const message =
      currentPolicy?.recommended_message ||
      intl.formatMessage(forceUpdateMessages.modalMessage);
    return (
      <ForceUpdateModal
        title={intl.formatMessage(forceUpdateMessages.modalTitle)}
        message={message}
        isInstalling={isInstalling}
        onUpdateNow={handleUpdateNow}
        actionText={intl.formatMessage(forceUpdateMessages.installAndRelaunch)}
      />
    );
  }

  if (evaluationResult === POLICY_EVALUATION.GRACE) {
    const deadlineAt = currentPolicy
      ? new Date(currentPolicy.force_update_deadline)
      : undefined;
    const formattedDeadline = formatUtcDate(deadlineAt, intl.locale || 'en');
    let message: string;
    const recommended = currentPolicy?.recommended_message;
    if (recommended) {
      if (formattedDeadline) {
        message = recommended.includes('{deadline}')
          ? recommended.replace('{deadline}', formattedDeadline)
          : `${recommended} ${formattedDeadline}`;
      } else {
        message = recommended;
      }
    } else {
      message = formattedDeadline
        ? intl.formatMessage(forceUpdateMessages.bannerMessageWithDeadline, {
            deadline: formattedDeadline,
          })
        : intl.formatMessage(forceUpdateMessages.bannerMessage);
    }
    return (
      <ForceUpdateBanner
        message={message}
        isInstalling={isInstalling}
        onUpdateNow={handleUpdateNow}
        actionText={intl.formatMessage(forceUpdateMessages.installAndRelaunch)}
      />
    );
  }
  return null;
};

export default ForceUpdate;
